import { createEmitter } from "ts_utils";
import { BrowserUtils } from "ts_utils";
import { AbstractPanelItem } from "./panel_items";

export class SongPanel {
	private _panelDiv:HTMLDivElement;
	private _navDiv:HTMLDivElement;
	
	/**map of panel indexes to PanelItems*/
	private _panelItems = new Map<number, AbstractPanelItem>();
	private _minItemIndex:number = 0;
	private _maxItemIndex:number = null;
	
	/**index of the item that should currently appear first in the panel*/
	private _currentIndex:number;
	private _currentlyDisplayedItems:Array<AbstractPanelItem> = [];
	
	private _prevLink:HTMLAnchorElement;
	private _nextLink:HTMLAnchorElement;
	
	private _navQueryPrefix:string;
	
	private _lastIndexRequest:number;
	
	private _maxDisplayedItems:number = 10;
	
	private _itemsPerRequestCall:number;
	
	/**Gets a stream of integers representing the first index number of the*/
	public get itemsRequest() { return this._itemsRequestEmitter.event; }
	private _itemsRequestEmitter = createEmitter<number>();
	
	/**Gets a stream of events that occur each time the panel's contents are
	 * updated.*/
	public get displayUpdate() { return this._displayUpdateEmitter.event; }
	private _displayUpdateEmitter = createEmitter<SongPanel>();
	
	constructor(panelDiv:HTMLDivElement, navDiv:HTMLDivElement,
			{ navQueryPrefix = "index", currentIndex = 0 } :
			{ navQueryPrefix?:string, currentIndex?:number }){
		this._panelDiv = panelDiv;
		this._navDiv = navDiv;
		this._navQueryPrefix = navQueryPrefix;
		this._currentIndex = currentIndex;
		
		this._init();
	}
	
	private _init():void {
		this._prevLink = this._navDiv.querySelector(".m-previousResults");
		this._nextLink = this._navDiv.querySelector(".m-nextResults");
		
		this._prevLink.addEventListener("click", this._onPrevLinkClick);
		this._nextLink.addEventListener("click", this._onNextLinkClick);
		
		this._updateNavDisplay();
	}
	
	public resetPanel():void {
		this._minItemIndex = 0;
		this._maxItemIndex = null;
		this._removePanelItemsFromDisplay();
		this._panelItems = new Map<number, AbstractPanelItem>();
		this._updateNavDisplay();
		this._currentIndex = 0;
		this._lastIndexRequest = null;
	}
	
	/**adds a list of MSPanelItem objects that may be displayed in the panel.
	 * [items] the list of MSPanelItem objects to add*/
	public addPanelItems(items:AbstractPanelItem[]):void {
		items.forEach((item) => this._panelItems.set(item.index, item));
		this.displayFromIndex(this._currentIndex);
	}
	
	/**displays panel items from the requested [index]. If [index] is null,
	 * the [_currentIndex] is used.*/
	public displayFromIndex(index:number):void {
		//make sure we're on the display, as otherwise the width calculation is
		//useless.
		if (!this._isAttachedToBody()){
			return;
		}
		
		let scrollY:number = window.scrollY;
		
		if (index == null){
			index = this._currentIndex;
		}
		else {
			this._currentIndex = index;
		}
		
		this._removePanelItemsFromDisplay();
		
		let panelRect = this._panelDiv.getBoundingClientRect();
		while (this._currentlyDisplayedItems.length < this._maxDisplayedItems){
			//the maxDisplayedItems is a guarantee against browsers which might
			//not support flexbox and thus our bounds checking - we don't want
			//to display thousands of items and generate tons of requests
			if (this._panelItems.has(index)){
				let item = this._panelItems.get(index);
				this._panelDiv.append(item.ui);
				let itemRect = item.ui.getBoundingClientRect();
				if (itemRect.right > panelRect.right &&
						this._panelDiv.children.length > 1){
					item.ui.remove();
					break;
				}
				this._currentlyDisplayedItems.push(item);
			}
			else {
				//this._requestItems(index);
				break;
			}
			index++;
		}
		
		this._updateNavDisplay();
		this._checkForNeededItems();
		
		//removing the panel items and nav causes scrolling to jump, so we store
		//it and reset after finishing these operations.
		window.scrollTo(0, scrollY);
		
		this._displayUpdateEmitter.emit(this);
	}
	
	public reportItemRequestFail():void {
		//reset this so we can try this index again.
		this._lastIndexRequest = null;
	}
	
	public getAllPanelItems():Array<AbstractPanelItem>{
		let items:Array<AbstractPanelItem> = [];
		let keys = Array.from(this._panelItems.keys());
		keys = keys.sort((i1, i2) => i1 <= i2 ? -1 : 1);
		for (let key of keys){
			items.push(this._panelItems.get(key));
		}
		return items;
	}
	
	private _updateNavDisplay():void {
		if (this._panelItems.size === 0){
			this._navDiv.style.display = "none";
			return;
		}
		this._navDiv.style.display = "";
		this._nextLink.removeAttribute('href');
		this._nextLink.removeAttribute('rel');
		let nextDiv = this._nextLink.querySelector('div');
		if (!nextDiv.classList.contains('disabled')) 
			nextDiv.classList.add('disabled');
		
		this._prevLink.removeAttribute('href');
		this._prevLink.removeAttribute('rel');
		let prevDiv = this._prevLink.querySelector('div');
		if (!prevDiv.classList.contains('disabled'))
			prevDiv.classList.add('disabled');
		
		let numVisible:number = this._panelDiv.children.length;
		if (this._maxItemIndex == null || this._maxItemIndex > numVisible + 
				this._currentIndex - 1){
			let index:number = this._currentIndex + numVisible;
			//make sure we have an item for this index
			if (this._panelItems.get(index) != null){
				this._nextLink.href =`./?${this._navQueryPrefix}=${index}`;
				this._nextLink.rel = "next";
				nextDiv.classList.remove('disabled');
			}
		}
		
		if (this._minItemIndex < this._currentIndex){
			let index:number = Math.max(this._currentIndex -
				this._maxDisplayedItems, this._minItemIndex);
			//guard against the situation where we have previous items but not
			//enough to match numVisible
			index = Math.max(index, this._getSortedPanelItemIndexes()[0]);
			//make sure we have an item for this index
			if (this._panelItems.get(index) != null){
				this._prevLink.href = `./?${this._navQueryPrefix}=${index}`;
				this._prevLink.rel = "prev";
				prevDiv.classList.remove('disabled');
			}
		}
	}
	
	private _checkForNeededItems():void {
		if (this._currentlyDisplayedItems.length === 0) return;
		
		let indexes:number[] = this._getSortedPanelItemIndexes();
		
		let firstDisplayed:number = this._currentlyDisplayedItems[0].index;
		let lastDisplayed:number = this._currentlyDisplayedItems[
			this._currentlyDisplayedItems.length - 1].index;
		
		if (firstDisplayed === indexes[0] &&
				firstDisplayed > this._minItemIndex){
			let targetIndex:number = (this._itemsPerRequestCall != null)
				? firstDisplayed - this._itemsPerRequestCall
				: firstDisplayed - this._currentlyDisplayedItems.length;
			this._requestItems(Math.max(this._minItemIndex, targetIndex));
		}
		else if (lastDisplayed === indexes[indexes.length - 1] &&
				(this._maxItemIndex == null ||
				lastDisplayed < this._maxItemIndex)){
			this._requestItems(lastDisplayed + 1);
		}
	}
	
	private _requestItems(index:number):void {
		if (this._lastIndexRequest === index) return; //avoid duplicates
		if (index >= this._minItemIndex && 
				(this._maxItemIndex == null || index <= this._maxItemIndex)){
			this._lastIndexRequest = index;
			this._itemsRequestEmitter.emit(index);
		}
	}
	
	private _onPrevLinkClick = (e:MouseEvent):void => {
		e.preventDefault();
		if (this._prevLink.href === "") return;
		
		let index = BrowserUtils.getQueryParam(this._navQueryPrefix,
			this._prevLink.href);
		this.displayFromIndex(Number.parseInt(index));
	}
	
	private _onNextLinkClick = (e:MouseEvent):void => {
		e.preventDefault();
		if (this._nextLink.href === "") return;
		
		let index = BrowserUtils.getQueryParam(this._navQueryPrefix,
			this._nextLink.href);
		this.displayFromIndex(Number.parseInt(index));
	}
	
	private _removePanelItemsFromDisplay():void {
		for (let i = this._panelDiv.children.length - 1; i >= 0; i--){
			this._panelDiv.children[i].remove();
		}
		this._currentlyDisplayedItems = [];
	}
	
	/**gets a sorted [List<number>] of indexes of all _panelItems available (not
	 * just displayed)*/
	private _getSortedPanelItemIndexes():Array<number> {
		let indexes = Array.from(this._panelItems.keys());
		indexes.sort((i1, i2) => (i1 <= i2)? -1 : 1);
		return indexes;
	}
	
	private _isAttachedToBody():boolean {
		return (document.contains(this._panelDiv) &&
			this._panelDiv.getBoundingClientRect().right > 0);
	}
	
	/**index of the first panel item that can be displayed - used for
	 * determining whether to show prev/next links. Defaults to 0.*/
	public get minItemIndex():number { return this._minItemIndex; }
	public set minItemIndex(value:number) {
		this._minItemIndex = value;
		this._updateNavDisplay();
	}
	
	/**index of the last panel item that can be displayed - used for determining
	 * whether to show prev/next links. If this value is null (default), it 
	 * means the max is still unknown.*/
	public get maxItemIndex():number { return this._maxItemIndex; }
	public set maxItemIndex(value:number) {
		this._maxItemIndex = value;
		this._updateNavDisplay();
	}
	
	/**returns the number of items currently displayed.*/
	public get numItemsDisplayed():number {
		return this._currentlyDisplayedItems.length;
	}
	
	/**the number of new results we expect to receive upon fulfillment of a 
	 * request for new items. This is null by default, meaning it won't be used.
	 * Setting this gives optimized handling when clicking previous, since we
	 * know how far to go back.*/
	public get itemsPerRequestCall():number {
		return this._itemsPerRequestCall;
	}
	public set itemsPerRequestCall(value:number) { 
		this._itemsPerRequestCall = value;
	}
}